// components/SyncStatusMenu.tsx
import React, { useState } from "react";
import { 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  IconButton, 
  Tooltip,
  CircularProgress,
  Typography,
  Box
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import HearingIcon from "@mui/icons-material/Hearing";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { colors, components } from "../theme";

export interface SyncStatus {
  hearing: boolean | null | 'pending'; // null = loading, true = synced, false = not synced, 'pending' = sync in progress
  optics: boolean | null | 'pending';
}

interface SyncStatusMenuProps {
  onCheckSyncStatus: () => Promise<SyncStatus>;
  onForceSync: (system: 'hearing' | 'optics') => Promise<void>;
  disabled?: boolean;
}

export const SyncStatusMenu: React.FC<SyncStatusMenuProps> = ({
  onCheckSyncStatus,
  onForceSync,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ hearing: null, optics: null });
  const [loading, setLoading] = useState(false);
  const [forceSyncLoading, setForceSyncLoading] = useState<{ hearing: boolean; optics: boolean }>({
    hearing: false,
    optics: false
  });
  const open = Boolean(anchorEl);

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    setSyncStatus({ hearing: null, optics: null });
    
    try {
      const status = await onCheckSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error('Failed to check sync status:', error);
      setSyncStatus({ hearing: false, optics: false });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSyncStatus({ hearing: null, optics: null });
  };

  const handleForceSync = async (system: 'hearing' | 'optics') => {
    setForceSyncLoading(prev => ({ ...prev, [system]: true }));
    
    try {
      await onForceSync(system);
      // After successful API call, set status to pending (sync will happen asynchronously)
      setSyncStatus(prev => ({ ...prev, [system]: 'pending' }));
    } catch (error) {
      console.error(`Failed to force sync ${system}:`, error);
      // On error, keep the current status (likely false)
    } finally {
      setForceSyncLoading(prev => ({ ...prev, [system]: false }));
    }
  };

  const getStatusColor = (status: boolean | null | 'pending') => {
    if (status === null) return colors.text.secondary;
    if (status === 'pending') return '#ff9800'; // amber for pending
    return status ? '#4caf50' : '#f44336'; // green for synced, red for not synced
  };

  const getStatusText = (status: boolean | null | 'pending') => {
    if (status === null) return 'Checking...';
    if (status === 'pending') return 'Sync Pending';
    return status ? 'Synced' : 'Not Synced';
  };

  return (
    <>
      <Tooltip title="Check sync status">
        <IconButton 
          color="primary" 
          onClick={handleClick} 
          size="large"
          disabled={disabled}
        >
          <SyncIcon
            sx={{
              fontSize: components.icon.large.width,
              color: colors.primary.main,
            }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
      >
        <MenuItem 
          onClick={syncStatus.hearing === false ? () => handleForceSync('hearing') : undefined}
          disabled={loading || syncStatus.hearing === null || syncStatus.hearing === true || syncStatus.hearing === 'pending' || forceSyncLoading.hearing}
          sx={{
            cursor: syncStatus.hearing === false ? 'pointer' : 'default',
            '&:hover': {
              backgroundColor: syncStatus.hearing === false ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
            }
          }}
        >
          <ListItemIcon>
            {loading || syncStatus.hearing === null ? (
              <CircularProgress size={20} />
            ) : forceSyncLoading.hearing ? (
              <CircularProgress size={20} />
            ) : (
              <HearingIcon 
                fontSize="small" 
                sx={{ color: getStatusColor(syncStatus.hearing) }}
              />
            )}
          </ListItemIcon>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2">Hearing System</Typography>
            <Typography 
              variant="caption" 
              sx={{ color: getStatusColor(syncStatus.hearing) }}
            >
              {forceSyncLoading.hearing ? 'Syncing...' : getStatusText(syncStatus.hearing)}
            </Typography>
          </Box>
        </MenuItem>
        
        <MenuItem 
          onClick={syncStatus.optics === false ? () => handleForceSync('optics') : undefined}
          disabled={loading || syncStatus.optics === null || syncStatus.optics === true || syncStatus.optics === 'pending' || forceSyncLoading.optics}
          sx={{
            cursor: syncStatus.optics === false ? 'pointer' : 'default',
            '&:hover': {
              backgroundColor: syncStatus.optics === false ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
            }
          }}
        >
          <ListItemIcon>
            {loading || syncStatus.optics === null ? (
              <CircularProgress size={20} />
            ) : forceSyncLoading.optics ? (
              <CircularProgress size={20} />
            ) : (
              <VisibilityIcon 
                fontSize="small" 
                sx={{ color: getStatusColor(syncStatus.optics) }}
              />
            )}
          </ListItemIcon>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2">Optics System</Typography>
            <Typography 
              variant="caption" 
              sx={{ color: getStatusColor(syncStatus.optics) }}
            >
              {forceSyncLoading.optics ? 'Syncing...' : getStatusText(syncStatus.optics)}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
};